import { useEffect, useMemo, useRef, useState } from "react";
import { Edit, Eye, EyeOff, Plus, RefreshCw, Search, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { uploadThumbnailToCOS } from "@/lib/cos-upload";
import {
  getAllKnownCategoryOptions,
  getCategoryLabel,
  normalizeCategoryList,
} from "@/lib/category-config";
import { addCustomCategoryOption, useCategoryOptions } from "@/hooks/useCategoryOptions";

interface SeriesItem {
  id: string;
  title: string;
  description: string | null;
  cover_url: string | null;
  created_at: string;
  updated_at: string;
  is_hidden?: boolean;
  categories?: string[];
  jlpt_levels?: string[];
  content_type?: "video" | "audio";
  video_count?: number;
}

const JLPT_LEVELS = ["N5", "N4", "N3", "N2", "N1"] as const;

type SeriesForm = {
  title: string;
  description: string;
  cover_url: string;
  categories: string[];
  jlpt_levels: string[];
  content_type: "video" | "audio";
  is_hidden: boolean;
};

const defaultForm: SeriesForm = {
  title: "",
  description: "",
  cover_url: "",
  categories: [],
  jlpt_levels: [],
  content_type: "video",
  is_hidden: false,
};

function normalizeSeriesPayload(form: SeriesForm) {
  return {
    title: form.title.trim(),
    description: form.description.trim() || null,
    cover_url: form.cover_url.trim() || null,
    categories: normalizeCategoryList(form.categories),
    jlpt_levels: Array.from(new Set(form.jlpt_levels.filter(Boolean))),
    content_type: form.content_type,
    is_hidden: form.is_hidden,
  };
}

export default function SeriesManagement() {
  const { toast } = useToast();

  const [seriesList, setSeriesList] = useState<SeriesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingSeries, setEditingSeries] = useState<SeriesItem | null>(null);
  const [formData, setFormData] = useState<SeriesForm>(defaultForm);

  const coverInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const { data: categoryOptions = getAllKnownCategoryOptions(), refetch: refetchCategoryOptions } =
    useCategoryOptions();

  const fetchSeries = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("series")
        .select(`
          *,
          videos:videos(count),
          audios:audios(count)
        `)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      const list = (data || []).map((item: any) => ({
        ...item,
        video_count: (item.videos?.[0]?.count || 0) + (item.audios?.[0]?.count || 0),
      })) as SeriesItem[];

      setSeriesList(list);
    } catch (error) {
      console.error("Failed to fetch series", error);
      toast({ title: "获取系列失败", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeries();
  }, []);

  const createCategory = (input: string) => {
    const created = addCustomCategoryOption(input);
    if (!created) return null;

    void refetchCategoryOptions();
    toast({ title: "分类已创建", description: `${created.label} 已加入可选列表` });
    return created;
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setEditingSeries(null);
  };

  const openEdit = (item: SeriesItem) => {
    setEditingSeries(item);
    setFormData({
      title: item.title || "",
      description: item.description || "",
      cover_url: item.cover_url || "",
      categories: Array.isArray(item.categories) ? item.categories : [],
      jlpt_levels: Array.isArray(item.jlpt_levels) ? item.jlpt_levels : [],
      content_type: item.content_type === "audio" ? "audio" : "video",
      is_hidden: !!item.is_hidden,
    });
    setEditOpen(true);
  };

  const handleUploadCover = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingCover(true);
    try {
      const { success, url, error } = await uploadThumbnailToCOS(file);
      if (!success || !url) {
        throw new Error(error || "Cover upload failed");
      }

      setFormData((prev) => ({ ...prev, cover_url: url }));
      toast({ title: "封面上传成功" });
    } catch (error: any) {
      console.error("Failed to upload cover", error);
      toast({ title: "封面上传失败", description: error?.message, variant: "destructive" });
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast({ title: "请输入系列标题", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const payload = normalizeSeriesPayload(formData);
      const { error } = await supabase.from("series").insert(payload as any);
      if (error) throw error;

      toast({ title: "系列创建成功" });
      setCreateOpen(false);
      resetForm();
      fetchSeries();
    } catch (error) {
      console.error("Failed to create series", error);
      toast({ title: "创建失败", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingSeries || !formData.title.trim()) return;

    setSaving(true);
    try {
      const payload = normalizeSeriesPayload(formData);
      const { error } = await supabase.from("series").update(payload as any).eq("id", editingSeries.id);
      if (error) throw error;

      toast({ title: "系列更新成功" });
      setEditOpen(false);
      resetForm();
      fetchSeries();
    } catch (error) {
      console.error("Failed to update series", error);
      toast({ title: "更新失败", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("确定删除该系列吗？该系列下视频会保留，但将取消系列归属。")) {
      return;
    }

    try {
      await supabase.from("videos").update({ series_id: null, series_order: null }).eq("series_id", id);
      await supabase.from("audios").update({ series_id: null, series_order: null }).eq("series_id", id);

      const { error } = await supabase.from("series").delete().eq("id", id);
      if (error) throw error;

      toast({ title: "系列已删除" });
      fetchSeries();
    } catch (error) {
      console.error("Failed to delete series", error);
      toast({ title: "删除失败", variant: "destructive" });
    }
  };

  const toggleHidden = async (item: SeriesItem, hidden: boolean) => {
    setSeriesList((prev) => prev.map((row) => (row.id === item.id ? { ...row, is_hidden: hidden } : row)));

    try {
      const { error } = await supabase.from("series").update({ is_hidden: hidden }).eq("id", item.id);
      if (error) throw error;

      toast({ title: hidden ? "已隐藏系列" : "已显示系列" });
    } catch (error) {
      setSeriesList((prev) => prev.map((row) => (row.id === item.id ? { ...row, is_hidden: item.is_hidden } : row)));
      console.error("Failed to toggle hidden", error);
      toast({ title: "更新失败", variant: "destructive" });
    }
  };

  const filteredSeries = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    if (!keyword) return seriesList;

    return seriesList.filter((item) => {
      return (
        item.title.toLowerCase().includes(keyword) ||
        (item.description || "").toLowerCase().includes(keyword)
      );
    });
  }, [seriesList, searchQuery]);

  const FormBlock = () => (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label>标题</Label>
        <Input
          value={formData.title}
          onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
          placeholder="例如：初级听力入门"
        />
      </div>

      <div className="space-y-2">
        <Label>描述</Label>
        <Textarea
          value={formData.description}
          onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
          placeholder="系列简介"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>JLPT</Label>
          <MultiSelect
            options={JLPT_LEVELS.map((level) => ({ value: level, label: level }))}
            selected={formData.jlpt_levels}
            onChange={(selected) =>
              setFormData((prev) => ({
                ...prev,
                jlpt_levels: Array.from(new Set(selected.filter(Boolean))),
              }))
            }
            placeholder="选择等级"
          />
        </div>

        <div className="space-y-2">
          <Label>分类</Label>
          <MultiSelect
            options={categoryOptions}
            selected={formData.categories}
            onChange={(selected) =>
              setFormData((prev) => ({
                ...prev,
                categories: normalizeCategoryList(selected),
              }))
            }
            allowCreate
            onCreateOption={createCategory}
            createText="新增分类"
            searchPlaceholder="搜索或新增分类"
            placeholder="选择分类"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>默认播放模式</Label>
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant={formData.content_type === "video" ? "default" : "outline"}
            onClick={() => setFormData((prev) => ({ ...prev, content_type: "video" }))}
          >
            视频模式
          </Button>
          <Button
            type="button"
            variant={formData.content_type === "audio" ? "default" : "outline"}
            onClick={() => setFormData((prev) => ({ ...prev, content_type: "audio" }))}
          >
            音频模式
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>封面</Label>
        {formData.cover_url ? (
          <div className="relative overflow-hidden rounded-md border bg-muted aspect-video">
            <img src={formData.cover_url} alt="cover" className="h-full w-full object-cover" />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 h-7 w-7 bg-black/60 text-white hover:bg-black/70"
              onClick={() => setFormData((prev) => ({ ...prev, cover_url: "" }))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ) : null}

        <div className="flex gap-2">
          <Input
            value={formData.cover_url}
            onChange={(event) => setFormData((prev) => ({ ...prev, cover_url: event.target.value }))}
            placeholder="输入封面 URL 或直接上传"
          />

          <input
            ref={coverInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleUploadCover}
          />
          <Button type="button" variant="outline" size="icon" onClick={() => coverInputRef.current?.click()}>
            {uploadingCover ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div>
          <p className="font-medium">前台显示</p>
          <p className="text-sm text-muted-foreground">关闭后该系列仅管理员可见</p>
        </div>
        <Switch
          checked={!formData.is_hidden}
          onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_hidden: !checked }))}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">系列管理</h1>
          <p className="text-muted-foreground">统一管理系列封面、分类和前台可见性</p>
        </div>

        <Dialog
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              新建系列
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>创建系列</DialogTitle>
            </DialogHeader>
            <FormBlock />
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                取消
              </Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
                创建
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-10"
                placeholder="搜索系列标题"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchSeries}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>系列列表 ({filteredSeries.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>标题</TableHead>
                  <TableHead>分类</TableHead>
                  <TableHead>资源数</TableHead>
                  <TableHead>前台可见</TableHead>
                  <TableHead>更新时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSeries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      暂无系列
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSeries.map((item) => {
                    const categories = normalizeCategoryList(item.categories || []);

                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {item.cover_url ? (
                              <img
                                src={item.cover_url}
                                alt={item.title}
                                className="h-10 w-16 rounded object-cover"
                              />
                            ) : null}
                            <div>
                              <p className="font-medium">{item.title}</p>
                              {item.description ? (
                                <p className="line-clamp-1 text-xs text-muted-foreground">{item.description}</p>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {categories.length > 0 ? (
                              categories.map((category) => (
                                <Badge key={category} variant="outline" className="text-xs">
                                  {getCategoryLabel(category)}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">未分类</span>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          <Badge variant="secondary">{item.video_count || 0}</Badge>
                        </TableCell>

                        <TableCell>
                          <Switch
                            checked={!item.is_hidden}
                            onCheckedChange={(checked) => toggleHidden(item, !checked)}
                          />
                        </TableCell>

                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(item.updated_at || item.created_at).toLocaleDateString()}
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                              <Edit className="h-4 w-4" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleHidden(item, !item.is_hidden)}
                            >
                              {item.is_hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </Button>

                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={() => handleDelete(item.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>编辑系列</DialogTitle>
          </DialogHeader>
          <FormBlock />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              取消
            </Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
